import {Request, sortBranches} from "../../Utils";
import Logger from "../../Utils/Logger";

// Parent (virtual) services are not linked to any branch, so the branch call does not return them.
// Find the parents of the returned sub-services through their tags and fetch them from the full list.
const getMissingParentServices = async (services) => {
    const host = window.constants.REACT_APP_API_URL;
    const existingParentIds = new Set(
        services.filter(s => s.tags?.SubServiceId).map(s => s.tags.SubServiceId)
    );
    const missingParentIds = new Set(
        services
            .filter(s => s.tags?.SubServiceGroupId && !s.tags?.SubServiceId && !existingParentIds.has(s.tags.SubServiceGroupId))
            .map(s => s.tags.SubServiceGroupId)
    );
    if (missingParentIds.size === 0) {
        return [];
    }

    try {
        // Request.get fetches all pages by itself
        const result = await Request.get(host + `/service/`, { body: { pageSize: 100 } });
        const allServices = Array.isArray(result?.data) ? result.data : [];

        return allServices.filter(s => s.tags?.SubServiceId && missingParentIds.has(s.tags.SubServiceId));
    } catch (e) {
        Logger.log(e, "getMissingParentServices error");
        return [];
    }
};

const getService = async (params = {}) => {
    const host = window.constants.REACT_APP_API_URL;
    const servicesResult = await Request.get(host + `/service/`, params);
    if (params?.body?.BranchId && Array.isArray(servicesResult?.data)) {
        const parentServices = await getMissingParentServices(servicesResult.data);
        servicesResult.data.push(...parentServices);
    }
    if (Array.isArray(servicesResult?.data)) {
        const serviceIds = servicesResult?.data.map((service) => '&TargetId=' + service.id);
        const urlParams = `?TargetName=Service${serviceIds.join("")}`
        const translations = await Request.get(host + `/translation/${urlParams}`, { body: { pageSize: 100 } })
        if (Array.isArray(translations?.data)) {
            for (let i = 0; i < servicesResult.data.length; i++) {
                const nameEn = translations.data.find(t => t.targetId === servicesResult.data[i].id && t.name === 'Name' && t.language === 'en-US');
                const nameEl = translations.data.find(t => t.targetId === servicesResult.data[i].id && t.name === 'Name' && t.language === 'el-GR');
                const descriptionEn = translations.data.find(t => t.targetId === servicesResult.data[i].id && t.name === 'Description' && t.language === 'en-US');
                const descriptionEl = translations.data.find(t => t.targetId === servicesResult.data[i].id && t.name === 'Description' && t.language === 'el-GR');
                servicesResult.data[i].name_en = nameEn?.value || servicesResult.data[i].name;
                servicesResult.data[i].name_gr = nameEl?.value || servicesResult.data[i].name;
                servicesResult.data[i].description_en = descriptionEn?.value || '';
                servicesResult.data[i].description_gr = descriptionEl?.value || '';
            }
        }

        const dynamicFieldIds = new Set();
        for (const service of servicesResult.data) {
            if (!Array.isArray(service.dynamicFields)) {
                continue;
            }
            for (const field of service.dynamicFields) {
                if (field.dynamicFieldId) {
                    dynamicFieldIds.add(field.dynamicFieldId);
                }
            }
        }

        if (dynamicFieldIds.size > 0) {
            const dynamicFieldUrlParams = `?TargetName=DynamicField${[...dynamicFieldIds].map((id) => '&TargetId=' + id).join('')}`;
            const dynamicFieldTranslations = await Request.get(host + `/translation/${dynamicFieldUrlParams}`, {});

            if (Array.isArray(dynamicFieldTranslations?.data)) {
                for (const service of servicesResult.data) {
                    if (!Array.isArray(service.dynamicFields)) {
                        continue;
                    }

                    service.dynamicFields = service.dynamicFields.map((field) => {
                        const labelEn = dynamicFieldTranslations.data.find(
                            (translation) => translation.targetId === field.dynamicFieldId && translation.name === 'Label' && translation.language === 'en-US'
                        );
                        const labelEl = dynamicFieldTranslations.data.find(
                            (translation) => translation.targetId === field.dynamicFieldId && translation.name === 'Label' && translation.language === 'el-GR'
                        );

                        return {
                            ...field,
                            label_en: labelEn?.value || field.label,
                            label_gr: labelEl?.value || field.label,
                        };
                    });
                }
            }
        }
    }

    Logger.log(servicesResult, "getService result");
    return servicesResult;
};

const getBranches = async (serviceId) => {
    const host = window.constants.REACT_APP_API_URL;
    if (serviceId) {
        const params = {
            body: {
                ServiceId: serviceId,
            },
        };
        const branchesResult = await Request.get(host + `/branch/`, params);
        if (Array.isArray(branchesResult?.data)) {
            const branchIds = branchesResult?.data.map((branch) => '&TargetId=' + branch.id);
            const urlParams = `?TargetName=Branch${branchIds.join("")}`
            const translations = await Request.get(host + `/translation/${urlParams}`, {})
            if (Array.isArray(translations?.data)) {
                for (let i = 0; i < branchesResult.data.length; i++) {
                    const nameEn = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Name' && t.language === 'en-US');
                    const nameEl = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Name' && t.language === 'el-GR');
                    const addressEn = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Address' && t.language === 'en-US');
                    const addressEl = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Address' && t.language === 'el-GR');
                    branchesResult.data[i].name_en = nameEn?.value || branchesResult.data[i].name;
                    branchesResult.data[i].name_gr = nameEl?.value || branchesResult.data[i].name;
                    branchesResult.data[i].address_en = addressEn?.value || '';
                    branchesResult.data[i].address_gr = addressEl?.value || '';
                }
            }
            branchesResult.data = branchesResult.data.sort(sortBranches)
        }
        Logger.log(branchesResult, "getBranches result");
        return branchesResult;
    } else {
        const branchesResult = await Request.get(host + `/branch/`, {});
        Logger.log(branchesResult, "getBranches result");
        if (Array.isArray(branchesResult?.data)) {
            const branchIds = branchesResult?.data.map((branch) => '&TargetId=' + branch.id);
            const urlParams = `?TargetName=Branch${branchIds.join("")}`
            const translations = await Request.get(host + `/translation/${urlParams}`, {})
            if (Array.isArray(translations?.data)) {
                for (let i = 0; i < branchesResult.data.length; i++) {
                    const nameEn = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Name' && t.language === 'en-US');
                    const nameEl = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Name' && t.language === 'el-GR');
                    const addressEn = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Address' && t.language === 'en-US');
                    const addressEl = translations.data.find(t => t.targetId === branchesResult.data[i].id && t.name === 'Address' && t.language === 'el-GR');
                    branchesResult.data[i].name_en = nameEn?.value || branchesResult.data[i].name;
                    branchesResult.data[i].name_gr = nameEl?.value || branchesResult.data[i].name;
                    branchesResult.data[i].address_en = addressEn?.value || '';
                    branchesResult.data[i].address_gr = addressEl?.value || '';
                }
            }
            branchesResult.data = branchesResult.data.sort(sortBranches)
        }
        return branchesResult;
    }
};
const getGroupBranches = async (serviceIds) => {
    const host = window.constants.REACT_APP_API_URL;
    if (!Array.isArray(serviceIds) || serviceIds.length === 0) {
        return [];
    }

    try {
        const requests = serviceIds.map((sid) => {
            const params = {
                body: {
                    ServiceId: sid,
                },
            };
            // Attach the serviceId to the resolved value so we can inject it later
            return Request.get(host + `/branch/`, params)
                .then((res) => ({ serviceId: sid, res }))
                .catch((error) => {
                    Logger.log(error, `getGroupBranches error for ServiceId ${sid}`);
                    return { serviceId: sid, res: null };
                });
        });

        const results = await Promise.all(requests);
        const combined = [];
        // A branch should be linked to only one service of the group. If more are linked, keep the first one.
        const addedBranchIds = new Set();
        results.forEach(({ serviceId, res }) => {
            const branches = Array.isArray(res?.data) ? res.data : [];
            branches.forEach((branch) => {
                if (addedBranchIds.has(branch.id)) {
                    return;
                }
                addedBranchIds.add(branch.id);
                combined.push({ ...branch, serviceId: serviceId });
            });
        });
        if (Array.isArray(combined)) {
            const branchIds = combined.map((branch) => '&TargetId=' + branch.id);
            const urlParams = `?TargetName=Branch${branchIds.join("")}`
            const translations = await Request.get(host + `/translation/${urlParams}`, {})
            if (Array.isArray(translations?.data)) {
                for (let i = 0; i < combined.length; i++) {
                    const nameEn = translations.data.find(t => t.targetId === combined[i].id && t.name === 'Name' && t.language === 'en-US');
                    const nameEl = translations.data.find(t => t.targetId === combined[i].id && t.name === 'Name' && t.language === 'el-GR');
                    const addressEn = translations.data.find(t => t.targetId === combined[i].id && t.name === 'Address' && t.language === 'en-US');
                    const addressEl = translations.data.find(t => t.targetId === combined[i].id && t.name === 'Address' && t.language === 'el-GR');
                    combined[i].name_en = nameEn?.value || combined[i].name;
                    combined[i].name_gr = nameEl?.value || combined[i].name;
                    combined[i].address_en = addressEn?.value || '';
                    combined[i].address_gr = addressEl?.value || '';
                }
            }
            combined.sort(sortBranches)
        }
        Logger.log(combined, "getGroupBranches combined result");
        return combined;
    } catch (e) {
        Logger.log(e, "getGroupBranches unexpected error");
        return [];
    }
};

const getWaysOfAction = async (eventTypeId) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {};
    const result = await Request.get(
        host + `/eventtype/${eventTypeId}`,
        params
    );
    Logger.log(result, "getWaysOfAction result");
    return result;
};

const getAvailability = async (serviceId, selectedBranchId, startDay, endDay) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {
        body: {
            start: startDay,
            end: endDay,
            Branchid: selectedBranchId,
        },
    };
    const result = await Request.get(
        host + `/service/${serviceId}/availability`,
        params
    );
    Logger.log(result, "getAvailability result");
    return result;
};

const bookAppointment = async (serviceId, data) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {
        body: data,
    };
    const result = await Request.post(
        host + `/service/${serviceId}/book`,
        params
    );
    Logger.log(result, "bookAppointment result");
    return result;
};

const getAppointmentInfo = async (bookingCode, encodedPhoneNumber, email) => {
    //Fetching the appointment ids
    const host = window.constants.REACT_APP_API_URL;
    try {
        const params = {
            body: {
                EventNumber: bookingCode,
                PhoneNumber: encodedPhoneNumber,
                Email: email,
            },
        };
        const result = await Request.get(host + `/Event/validate`, params);
        Logger.log(result, "validate appointment result");
        return result;
    } catch (error) {
        console.error(
            "Error fetching appointment info:",
            error.message || error
        );
        return {
            status: "error",
            message: error.message || "An unknown error occurred",
        };
    }
};

const validateAppointment = async (bookingCode) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {
        body: {
            EventNumber: bookingCode,
            PhoneNumber: "+306986855787",
        },
    };
    const result = await Request.get(host + `/Event/validate`, params);
    Logger.log(result, "validate appointment result");
    return result;
};

const cancelAppointment = async (id, body) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {
        body: body,
    };
    const result = await Request.post(host + `/Event/${id}/cancel`, params);
    Logger.log(result, "cancel appointment result");
    return result;
};

const editAppointment = async (serviceId, data) => {
    const host = window.constants.REACT_APP_API_URL;
    const params = {
        body: data,
    };
    const result = await Request.put(host + `/Event/${serviceId}`, params);
    Logger.log(result, "edit appointment result");
    return result;
};

const mfaSend = async (data) => {
    const host = window.constants.REACT_APP_API_URL;
    const organizationResult = await Request.get(host + `/organisation/`);
    const organizationId = organizationResult?.data[0]?.id;
    const params = {
        body: { ...data,
            OrganisationId: organizationId
         },
    };
    const result = await Request.post(host + `/MFA/Send`, params);
    Logger.log(result, "mfaSend result");
    return result;
};

export const AppointmentService = {
    cancelAppointment,
    validateAppointment,
    getAppointmentInfo,
    getService,
    getBranches,
    getGroupBranches,
    getWaysOfAction,
    getAvailability,
    bookAppointment,
    editAppointment,
    mfaSend,
};
